import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { vehicleService } from '@/services/vehicles';
import type { VehicleMake, VehicleModel, VehicleVariant } from '@/services/vehicles';

export function useVehicleMakes(category?: string) {
  return useQuery({
    queryKey: ['vehicle-makes', category],
    queryFn: () => vehicleService.getMakes({ category }),
  });
}

export function useVehicleModels(makeId?: string) {
  return useQuery({
    queryKey: ['vehicle-models', makeId],
    queryFn: () => vehicleService.getModels(makeId),
    enabled: !!makeId,
  });
}

export function useVehicleVariants(modelId?: string) {
  return useQuery({
    queryKey: ['vehicle-variants', modelId],
    queryFn: () => vehicleService.getVariants(modelId),
    enabled: !!modelId,
  });
}

export function useVehicleSegments(category?: string | string[]) {
  // Normalise to an array so a multi-category Make (round-2 Wave C, item C2)
  // can request the combined (union) segment vocabulary in one query, while
  // a single category still works exactly as before.
  const categories = Array.isArray(category) ? category : category ? [category] : [];
  return useQuery({
    queryKey: ['vehicle-segments', categories.slice().sort().join(',')],
    queryFn: () => vehicleService.getSegments(categories),
    enabled: categories.length > 0,
  });
}

export function useCreateVehicleMake() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<VehicleMake>) => vehicleService.createMake(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-makes'] });
      toast.success('Vehicle make created');
    },
    onError: () => toast.error('Failed to create vehicle make'),
  });
}

export function useCreateVehicleModel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<VehicleModel>) => vehicleService.createModel(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-models'] });
      toast.success('Vehicle model created');
    },
    onError: () => toast.error('Failed to create vehicle model'),
  });
}

export function useCreateVehicleVariant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<VehicleVariant>) => vehicleService.createVariant(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-variants'] });
      toast.success('Vehicle variant created');
    },
    onError: () => toast.error('Failed to create vehicle variant'),
  });
}

export function useVehicleTypesLookup() {
  return useQuery({
    queryKey: ['vehicle-types', 'lookup'],
    queryFn: () => vehicleService.getTypesLookup(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useFuelTypesLookup() {
  return useQuery({
    queryKey: ['fuel-types', 'lookup'],
    queryFn: () => vehicleService.getFuelTypesLookup(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useFuelTypes() {
  return useQuery({
    queryKey: ['fuel-types'],
    queryFn: () => vehicleService.getFuelTypes(),
  });
}

export function useBodyTypes() {
  return useQuery({
    queryKey: ['body-types'],
    queryFn: () => vehicleService.getBodyTypes(),
  });
}
